import { useEffect, useState } from 'react';
import { Bell, AlertTriangle, AlertCircle, Info, MoreHorizontal, Eye, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  MetricCard,
} from '@/components/common';
import { alertsApi, Alert, QueryAlertParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const severityIcons: Record<string, React.ReactNode> = {
  CRITICAL: <AlertCircle className="h-4 w-4 text-red-500" />,
  HIGH: <AlertTriangle className="h-4 w-4 text-orange-500" />,
  MEDIUM: <Info className="h-4 w-4 text-amber-500" />,
  LOW: <Info className="h-4 w-4 text-blue-500" />,
};

const severityColors: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  HIGH: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  LOW: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

// Helper to derive status from alert state
type AlertStatus = 'active' | 'read' | 'dismissed';
const getAlertStatus = (alert: Alert): AlertStatus => {
  if (alert.isDismissed) return 'dismissed';
  if (alert.isRead) return 'read';
  return 'active';
};

const statusColors: Record<AlertStatus, string> = {
  active: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  read: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  dismissed: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
};

export default function Alerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchAlerts = async (params?: QueryAlertParams) => {
    try {
      setLoading(true);
      const response = await alertsApi.getAll(params);
      setAlerts(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params: QueryAlertParams = {};
    if (severityFilter !== 'all') {
      params.severity = severityFilter as Alert['severity'];
    }
    if (statusFilter === 'active') {
      params.isRead = false;
      params.isDismissed = false;
    } else if (statusFilter === 'read') {
      params.isRead = true;
      params.isDismissed = false;
    } else if (statusFilter === 'dismissed') {
      params.isDismissed = true;
    }
    fetchAlerts(params);
  }, [severityFilter, statusFilter]);

  const handleAcknowledge = async (alert: Alert) => {
    try {
      await alertsApi.acknowledge(alert.id);
      toast.success('Alert acknowledged');
      fetchAlerts();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDismiss = async (alert: Alert) => {
    try {
      await alertsApi.dismiss(alert.id);
      toast.success('Alert dismissed');
      fetchAlerts();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate metrics
  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL' && getAlertStatus(a) === 'active').length;
  const highCount = alerts.filter(a => a.severity === 'HIGH' && getAlertStatus(a) === 'active').length;
  const activeCount = alerts.filter(a => getAlertStatus(a) === 'active').length;
  const readCount = alerts.filter(a => getAlertStatus(a) === 'read').length;

  const columns: Column<Alert>[] = [
    {
      key: 'title',
      header: 'Alert',
      sortable: true,
      render: (alert) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
            {severityIcons[alert.severity]}
          </div>
          <div>
            <div className="font-medium">{alert.title}</div>
            <div className="text-sm text-muted-foreground line-clamp-1">
              {alert.message}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (alert) => (
        <Badge variant="outline">{alert.type.replace(/_/g, ' ')}</Badge>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (alert) => (
        <Badge className={severityColors[alert.severity]}>
          {alert.severity}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (alert) => {
        const status = getAlertStatus(alert);
        return (
          <Badge className={statusColors[status]}>
            {status.toUpperCase()}
          </Badge>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (alert) => (
        <span className="text-muted-foreground">{formatDate(alert.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (alert) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {getAlertStatus(alert) === 'active' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleAcknowledge(alert)}>
                  <Check className="mr-2 h-4 w-4" />
                  Acknowledge
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDismiss(alert)}>
                  <X className="mr-2 h-4 w-4" />
                  Dismiss
                </DropdownMenuItem>
              </>
            )}
            {getAlertStatus(alert) === 'read' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleDismiss(alert)}>
                  <X className="mr-2 h-4 w-4" />
                  Dismiss
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Alerts"
        description="Monitor and manage system alerts"
        breadcrumbs={[{ label: 'Alerts' }]}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Critical"
          value={criticalCount}
          icon={<AlertCircle className="h-5 w-5 text-red-500" />}
          trend={criticalCount > 0 ? { direction: 'up', value: '', text: 'requires immediate attention' } : undefined}
        />
        <MetricCard
          label="High Priority"
          value={highCount}
          icon={<AlertTriangle className="h-5 w-5 text-orange-500" />}
        />
        <MetricCard
          label="Active Alerts"
          value={activeCount}
          icon={<Bell className="h-5 w-5" />}
        />
        <MetricCard
          label="Read"
          value={readCount}
          icon={<Check className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={alerts}
        columns={columns}
        loading={loading}
        emptyTitle="No alerts found"
        emptyDescription="All systems are operating normally."
      />
    </div>
  );
}
